import type { MockBridge } from "./bridge";
import { DebugJsonWidget } from "./json-widget";

type SectionKey =
  | "Screenshots"
  | "Mock Player State"
  | "Mock Notifications"
  | "App Lifecycle"
  | "Onboardings"
  | "In-app-purchases"
  | "Message Log";

export class DebugMenu {
  private container: HTMLElement;
  private isVisible = false;
  private sectionOpenState: Partial<Record<SectionKey, boolean>> = {};
  private stateWidget: DebugJsonWidget;
  private isCapturingScreenshot = false;
  private screenshot?: string;
  private screenshotError?: string;

  constructor(private mockBridge: MockBridge) {
    this.stateWidget = new DebugJsonWidget(this.createButton.bind(this));
    this.container = document.createElement("div");
    this.setupContainer();
    this.setupToggleButton();
    this.mockBridge.setOnNotificationChange(() => this.render());
    this.mockBridge.setOnStateChange(() => this.render());
    this.render();
    document.body.appendChild(this.container);
  }

  private setupContainer() {
    this.container.style.position = "fixed";
    this.container.style.top = "0";
    this.container.style.right = "0";
    this.container.style.width = "300px";
    this.container.style.background = "rgba(0, 0, 0, 0.9)";
    this.container.style.color = "white";
    this.container.style.padding = "10px";
    this.container.style.fontFamily = "monospace";
    this.container.style.fontSize = "12px";
    this.container.style.zIndex = "10000";
    this.container.style.maxHeight = "100vh";
    this.container.style.overflowY = "auto";
    this.container.style.overflowX = "hidden";
    this.container.style.display = "none";
    this.container.setAttribute("data-testid", "debug-menu");
    this.container.setAttribute("data-test-id", "jest-sdk-mock");
  }

  private setupToggleButton() {
    const toggleBtn = document.createElement("button");
    toggleBtn.textContent = "JestSDK";
    toggleBtn.style.position = "fixed";
    toggleBtn.style.width = "auto";
    toggleBtn.style.top = "10px";
    toggleBtn.style.right = "10px";
    toggleBtn.style.zIndex = "10001";
    toggleBtn.style.padding = "5px 10px";
    toggleBtn.style.background = "#666d";
    toggleBtn.style.color = "white";
    toggleBtn.style.border = "none";
    toggleBtn.style.borderRadius = "4px";
    toggleBtn.style.cursor = "pointer";
    toggleBtn.setAttribute("data-test-id", "jest-sdk-mock-toggle");

    toggleBtn.addEventListener("click", () => this.toggle());
    document.body.appendChild(toggleBtn);
  }

  private toggle() {
    this.isVisible = !this.isVisible;
    this.container.style.display = this.isVisible ? "block" : "none";
  }

  private createButton(text: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.textContent = text;
    button.style.margin = "5px";
    button.style.padding = "5px 10px";
    button.style.background = "#444";
    button.style.color = "white";
    button.style.border = "none";
    button.style.borderRadius = "4px";
    button.style.cursor = "pointer";
    button.addEventListener("click", onClick);
    return button;
  }

  private createSelect<T extends string>(
    label: string,
    options: T[],
    currentValue: string,
    onChange: (value: T) => void,
  ): HTMLDivElement {
    const container = document.createElement("div");
    container.style.margin = "10px 0";

    const labelElement = document.createElement("label");
    labelElement.textContent = label + ": ";
    labelElement.style.display = "block";
    labelElement.style.marginBottom = "5px";
    labelElement.style.color = "#ccc";
    container.appendChild(labelElement);

    const select = document.createElement("select");
    select.style.width = "100%";
    select.style.padding = "5px";
    select.style.background = "#444";
    select.style.color = "white";
    select.style.border = "1px solid #666";
    select.style.borderRadius = "4px";

    options.forEach((option) => {
      const optionElement = document.createElement("option");
      optionElement.value = option;
      optionElement.textContent = option;
      if (option === currentValue) {
        optionElement.selected = true;
      }
      select.appendChild(optionElement);
    });

    select.addEventListener("change", () => {
      onChange(select.value as T);
    });

    container.appendChild(select);
    return container;
  }

  private createSection(
    title: SectionKey,
    initiallyOpen = false,
  ): { section: HTMLDetailsElement; content: HTMLDivElement } {
    const section = document.createElement("details");
    const isOpen = this.sectionOpenState[title] ?? initiallyOpen;
    section.open = isOpen;
    section.style.marginBottom = "12px";
    section.style.borderTop = "1px solid #333";
    section.style.paddingTop = "8px";

    const summary = document.createElement("summary");
    summary.textContent = title;
    summary.style.cursor = "pointer";
    summary.style.userSelect = "none";
    summary.style.fontSize = "13px";
    summary.style.fontWeight = "bold";
    summary.style.margin = "0";
    summary.style.color = "white";

    section.appendChild(summary);

    const content = document.createElement("div");
    content.style.marginTop = "8px";
    content.style.paddingLeft = "12px";

    section.appendChild(content);

    this.sectionOpenState[title] = isOpen;

    section.addEventListener("toggle", () => {
      this.sectionOpenState[title] = section.open;
    });

    return { section, content };
  }

  private isQueryFlagEnabled(flag: string): boolean {
    return new URLSearchParams(window.location.search).get(flag) === "1";
  }

  private async captureScreenshot(): Promise<void> {
    if (this.isCapturingScreenshot) {
      return;
    }
    this.isCapturingScreenshot = true;
    this.screenshot = undefined;
    this.screenshotError = undefined;
    this.render();

    try {
      this.screenshot = await this.mockBridge.takeScreenshot();
    } catch (error) {
      this.screenshotError =
        error instanceof Error ? error.message : "Screenshot capture failed";
    } finally {
      this.isCapturingScreenshot = false;
      this.render();
    }
  }

  private renderScreenshots(): HTMLDetailsElement {
    const { section, content } = this.createSection("Screenshots");
    const captureButton = this.createButton(
      this.isCapturingScreenshot ? "Capturing…" : "Capture Screenshot",
      () => void this.captureScreenshot(),
    );
    captureButton.disabled = this.isCapturingScreenshot;
    content.appendChild(captureButton);

    if (this.screenshotError) {
      const error = document.createElement("p");
      error.setAttribute("role", "alert");
      error.textContent = this.screenshotError;
      error.style.color = "#ffaaaa";
      content.appendChild(error);
    } else if (this.screenshot) {
      const preview = document.createElement("img");
      preview.src = this.screenshot;
      preview.alt = "Captured game screenshot";
      preview.style.display = "block";
      preview.style.width = "100%";
      preview.style.height = "auto";
      content.appendChild(preview);
    }

    return section;
  }

  private render() {
    this.container.innerHTML = "";

    const { section: playerSection, content: playerContent } =
      this.createSection("Mock Player State", true);

    const playerNote = document.createElement("p");
    playerNote.textContent =
      "Note: Changes to login status or entry payload will reload the game.";
    playerNote.style.color = "#aaa";
    playerNote.style.fontSize = "11px";
    playerNote.style.marginTop = "0";
    playerNote.style.marginBottom = "8px";
    playerContent.appendChild(playerNote);

    const currentPlayer = this.mockBridge.getMockPlayer();
    const playerInfo = document.createElement("pre");
    playerInfo.style.whiteSpace = "pre-wrap";
    playerInfo.style.wordBreak = "break-word";
    playerInfo.textContent = JSON.stringify(currentPlayer, null, 2);
    playerContent.appendChild(playerInfo);

    // Player Controls
    const playerControls = document.createElement("div");

    // Login/Logout button
    const loginBtn = this.createButton(
      currentPlayer.registered ? "Logout" : "Login",
      () => {
        if (
          confirm(
            `This will ${currentPlayer.registered ? "logout" : "login"} the player and reload the game. Continue?`,
          )
        ) {
          this.mockBridge.setMockPlayer({
            registered: !currentPlayer.registered,
          });
        }
      },
    );
    playerControls.appendChild(loginBtn);

    // Reset player button
    const resetBtn = this.createButton(
      "Reset Player (clears all state)",
      () => {
        if (
          confirm(
            "This will reset all player state and reload the game. Continue?",
          )
        ) {
          this.mockBridge.resetPlayer();
        }
      },
    );
    playerControls.appendChild(resetBtn);

    // Player ID change with reload warning
    const changeIdBtn = this.createButton(
      "Change Player ID (reloads game)",
      () => {
        const newId = prompt("Enter new player ID:", currentPlayer.playerId);
        if (newId && newId !== currentPlayer.playerId) {
          if (confirm("This will reload the game. Continue?")) {
            this.mockBridge.setMockPlayer({ playerId: newId });
          }
        }
      },
    );
    playerControls.appendChild(changeIdBtn);

    // Entry payload (with reload warning)
    const updatePayloadBtn = this.stateWidget.createModalButton(
      "Update Entry Payload (reloads game)",
      {
        title: "Update Entry Payload",
        hint: "Entry payload must be a JSON object. Applying reloads the game.",
        initialValue: currentPlayer.entryPayload,
        applyLabel: "Apply & Reload",
        onApply: (value) => {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set("entryPayload", JSON.stringify(value));
          window.location.href = newUrl.toString();
        },
      },
    );
    playerControls.appendChild(updatePayloadBtn);

    // State manipulation controls
    const editStateBtn = this.stateWidget.createModalButton(
      "Edit Player State",
      {
        title: "Edit Player State",
        initialValue: currentPlayer.playerData,
        onApply: (value) => {
          this.mockBridge.updatePlayerData({ update: value });
          this.render();
        },
      },
    );
    playerControls.appendChild(editStateBtn);

    playerContent.appendChild(playerControls);

    // Notifications Section
    const { section: notificationsSection, content: notificationsContent } =
      this.createSection("Mock Notifications");

    const notifications = this.mockBridge.getStoredNotifications();
    const notificationsInfo = document.createElement("pre");
    notificationsInfo.style.maxHeight = "200px";
    notificationsInfo.style.overflowY = "auto";
    notificationsInfo.style.whiteSpace = "pre-wrap";
    notificationsInfo.style.wordBreak = "break-word";

    if (notifications.length === 0) {
      notificationsInfo.textContent = "No notifications scheduled";
    } else {
      notificationsInfo.textContent = JSON.stringify(notifications, null, 2);
    }

    const clearNotificationsBtn = this.createButton(
      "Clear Notifications",
      () => {
        if (confirm("Clear all scheduled notifications?")) {
          this.mockBridge.clearNotifications();
          this.render();
        }
      },
    );

    notificationsContent.appendChild(clearNotificationsBtn);
    notificationsContent.appendChild(notificationsInfo);

    const { section: lifecycleSection, content: lifecycleContent } =
      this.createSection("App Lifecycle");

    const lifecycleNote = document.createElement("p");
    lifecycleNote.textContent =
      "Note: onHide and onShow follow real browser visibility — switch tabs or lock the device to trigger them.";
    lifecycleNote.style.color = "#aaa";
    lifecycleNote.style.fontSize = "11px";
    lifecycleNote.style.marginTop = "0";
    lifecycleNote.style.marginBottom = "8px";
    lifecycleContent.appendChild(lifecycleNote);

    const requestExitBtn = this.createButton("Request Exit", () => {
      this.mockBridge.notifyPlatformExitRequested();
    });
    requestExitBtn.setAttribute("data-test-id", "jest-sdk-mock-request-exit");
    lifecycleContent.appendChild(requestExitBtn);

    // In-app-purchases Section
    const { section: purchasesSection, content: purchasesContent } =
      this.createSection("In-app-purchases");

    const purchaseResponseOptions: Array<
      "success" | "cancel" | "error:internal_error" | "error:invalid_product"
    > = ["success", "cancel", "error:internal_error", "error:invalid_product"];

    const purchaseSelect = this.createSelect(
      "Begin Purchase Response",
      purchaseResponseOptions,
      this.mockBridge.getMockPurchaseResponse(),
      (value) => {
        this.mockBridge.setMockPurchaseResponse(value);
        this.render(); // Re-render to update the select
      },
    );

    purchasesContent.appendChild(purchaseSelect);

    const completePurchaseResponseOptions: Array<
      "success" | "error:internal_error" | "error:invalid_token"
    > = ["success", "error:internal_error", "error:invalid_token"];

    const completePurchaseSelect = this.createSelect(
      "Complete Purchase Response",
      completePurchaseResponseOptions,
      this.mockBridge.getMockCompletePurchaseResponse(),
      (value) => {
        this.mockBridge.setMockCompletePurchaseResponse(value);
        this.render(); // Re-render to update the select
      },
    );

    purchasesContent.appendChild(completePurchaseSelect);

    // Incomplete Purchase toggle
    const incompletePurchaseContainer = document.createElement("div");
    incompletePurchaseContainer.style.margin = "10px 0";

    const incompletePurchaseLabel = document.createElement("span");
    incompletePurchaseLabel.textContent = "Mock Incomplete Purchase: ";
    incompletePurchaseContainer.appendChild(incompletePurchaseLabel);

    const incompletePurchaseOnButton = this.createButton("ON", () => {
      this.mockBridge.setMockIncompletePurchase(true);
      this.render();
    });

    const incompletePurchaseOffButton = this.createButton("OFF", () => {
      this.mockBridge.setMockIncompletePurchase(false);
      this.render();
    });

    // Set active state styling
    if (this.mockBridge.getMockIncompletePurchase()) {
      incompletePurchaseOnButton.style.backgroundColor = "#6a6";
      incompletePurchaseOffButton.style.backgroundColor = "#444";
    } else {
      incompletePurchaseOnButton.style.backgroundColor = "#444";
      incompletePurchaseOffButton.style.backgroundColor = "#6a6";
    }

    incompletePurchaseContainer.appendChild(incompletePurchaseOnButton);
    incompletePurchaseContainer.appendChild(incompletePurchaseOffButton);
    purchasesSection.appendChild(incompletePurchaseContainer);

    const editProductsBtn = this.createButton("Edit Products", () => {
      try {
        const currentProducts = this.mockBridge.getMockProducts();
        const newProducts = prompt(
          "Edit products (as JSON):",
          JSON.stringify(currentProducts, null, 2),
        );

        if (newProducts) {
          const parsed = JSON.parse(newProducts) as ReturnType<
            MockBridge["getMockProducts"]
          >;
          this.mockBridge.setMockProducts(parsed);
          this.render();
        }
      } catch (error) {
        alert(
          "Invalid JSON products: " +
            (error instanceof Error ? error.message : String(error)),
        );
      }
    });
    purchasesContent.appendChild(editProductsBtn);

    const shouldShowLog = this.isQueryFlagEnabled("jest_debug");
    const shouldShowOnboardings = this.isQueryFlagEnabled("jest_onboarding");
    let logSection: HTMLDetailsElement | null = null;
    let onboardingsSection: HTMLDetailsElement | null = null;

    if (shouldShowLog) {
      const logSectionInfo = this.createSection("Message Log");
      logSection = logSectionInfo.section;
      const logContent = logSectionInfo.content;

      const messageLog = this.mockBridge.getMessageLog();
      const logInfo = document.createElement("pre");
      logInfo.style.maxHeight = "200px";
      logInfo.style.overflowY = "auto";
      logInfo.style.whiteSpace = "pre-wrap";
      logInfo.style.wordBreak = "break-word";
      logInfo.textContent = JSON.stringify(messageLog, null, 2);

      const clearLogButton = this.createButton("Clear Log", () => {
        this.mockBridge.clearMessageLog();
        this.render();
      });

      logContent.appendChild(clearLogButton);
      logContent.appendChild(logInfo);
    }

    if (shouldShowOnboardings) {
      const onboardingSectionInfo = this.createSection("Onboardings");
      onboardingsSection = onboardingSectionInfo.section;
      const onboardingContent = onboardingSectionInfo.content;

      const rejectLoginLeasesContainer = document.createElement("div");
      rejectLoginLeasesContainer.style.margin = "10px 0";

      const rejectLoginLeasesLabel = document.createElement("span");
      rejectLoginLeasesLabel.textContent = "Reject Login Link Leases: ";
      rejectLoginLeasesContainer.appendChild(rejectLoginLeasesLabel);

      const onButton = this.createButton("ON", () => {
        this.mockBridge.setRejectLoginLeaseAcquisition(true);
        this.render();
      });

      const offButton = this.createButton("OFF", () => {
        this.mockBridge.setRejectLoginLeaseAcquisition(false);
        this.render();
      });

      if (this.mockBridge.getRejectLoginLeaseAcquisition()) {
        onButton.style.backgroundColor = "#6a6";
        offButton.style.backgroundColor = "#444";
      } else {
        onButton.style.backgroundColor = "#444";
        offButton.style.backgroundColor = "#6a6";
      }

      rejectLoginLeasesContainer.appendChild(onButton);
      rejectLoginLeasesContainer.appendChild(offButton);
      onboardingContent.appendChild(rejectLoginLeasesContainer);

      const skipLoginSmsContainer = document.createElement("div");
      skipLoginSmsContainer.style.margin = "10px 0";

      const skipLoginSmsLabel = document.createElement("span");
      skipLoginSmsLabel.textContent = "Skip login SMS: ";
      skipLoginSmsContainer.appendChild(skipLoginSmsLabel);

      const skipSmsOnButton = this.createButton("ON", () => {
        this.mockBridge.setSkipLoginSms(true);
        this.render();
      });

      const skipSmsOffButton = this.createButton("OFF", () => {
        this.mockBridge.setSkipLoginSms(false);
        this.render();
      });

      if (this.mockBridge.getSkipLoginSms()) {
        skipSmsOnButton.style.backgroundColor = "#6a6";
        skipSmsOffButton.style.backgroundColor = "#444";
      } else {
        skipSmsOnButton.style.backgroundColor = "#444";
        skipSmsOffButton.style.backgroundColor = "#6a6";
      }

      skipLoginSmsContainer.appendChild(skipSmsOnButton);
      skipLoginSmsContainer.appendChild(skipSmsOffButton);
      onboardingContent.appendChild(skipLoginSmsContainer);
    }

    // Append all sections
    this.container.appendChild(playerSection);
    this.container.appendChild(notificationsSection);
    this.container.appendChild(lifecycleSection);
    if (onboardingsSection) {
      this.container.appendChild(onboardingsSection);
    }
    this.container.appendChild(purchasesSection);
    if (logSection) {
      this.container.appendChild(logSection);
    }
    this.container.appendChild(this.renderScreenshots());
  }
}
